<template>
  <div>
    <ErrorsList
      :errors="errors"
      :class="$style.errors"
    />

    <div :class="$style.content">
      <div
        v-if="isLoading"
        :class="$style.loadBox"
      >
        <ProgressCircular :indeterminate="true" />
      </div>

      <template v-if="calendarData && calendarData.length">
        <div :class="$style.controls">
          <UiButton
            :class="$style.buttonPrevious"
            :disabled="paginationControls.previous.disabled"
            theme="gray"
            @click="loadCalendarPrevious"
          >
            <UiIcon
              :class="$style.buttonIcon"
              :color-inheritance="true"
              name="arrow-to-left"
            />
          </UiButton>

          <UiButton
            :disabled="paginationControls.next.disabled"
            :class="$style.buttonNext"
            theme="gray"
            @click="loadCalendarNext"
          >
            <UiIcon
              :class="$style.buttonIcon"
              :color-inheritance="true"
              name="arrow-to-right"
            />
          </UiButton>
        </div>

        <div :class="$style.list">
          <CalendarLessonsDay
            v-for="day in calendarData"
            :key="day.date"
            :class="{
              [$style.item]: true,
              [$style.itemOnly]: calendarData.length === 1,
            }"
            :day="day"
          >
            <template #button-content="scope">
              <slot
                name="day-button-content"
                v-bind="scope"
              />
            </template>

            <template #dropdown-content="scope">
              <slot
                name="day-dropdown-content"
                v-bind="scope"
              />
            </template>
          </CalendarLessonsDay>
        </div>
      </template>

      <div
        v-else-if="!isFirstLoad"
        :class="$style.emptyMessage"
      >
        {{ emptyMessage }}
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { MEDIA_PER_PAGE } from '@/constants/calendar';
  import { asyncDelay, getUserId } from '@/utils';
  import { mediaQueries } from '@/utils/ui';
  import UiButton from '@/components/UiButton';
  import UiIcon from '@/components/UiIcon';
  import ProgressCircular from '@/components/ProgressCircular';
  import ErrorsList from '@/components/ErrorsList';
  import CalendarLessonsDay from '@/components/CalendarLessons/CalendarLessonsDay';

  export default {
    name: 'CalendarLessons',

    components: {
      UiButton,
      UiIcon,
      ProgressCircular,
      ErrorsList,
      CalendarLessonsDay,
    },

    props: {
      user: {
        required: true,
        validator(prop) {
          return (prop === null || typeof prop === 'object');
        },
      },
      appUser: {
        type: String,
        default: '',
      },
      userType: {
        type: String,
        required: true,
      },
      paginationPrevious: {
        type: Boolean,
        default: true,
      },
      emptyMessage: {
        type: String,
        default: 'Список занятий пуст...',
      },
    },

    data: () => ({
      calendarData: [],
      pagination: null,
      timerErrors: 0,
      isLoading: true,
      isFirstLoad: true,
    }),

    computed: {
      ...mapGetters({
        data: 'calendar/getData',
        meta: 'calendar/getMeta',
        errors: 'calendar/getErrors',
      }),

      breakpoint() {
        return mediaQueries.breakpoint;
      },

      paginationControls() {
        const controls = {
          previous: {
            disabled: true,
          },
          next: {
            disabled: true,
          },
        };
        if (this.pagination) {
          const { offset, currentPage, lastPage } = this.pagination;
          controls.previous.disabled = !(
            Boolean(currentPage > 1)
            || (offset && offset > 0 && this.paginationPrevious)
          );
          controls.next.disabled = !((currentPage + 1) <= lastPage);
        }
        return controls;
      },
    },

    watch: {
      user: {
        handler(user) {
          if (!user) return;
          this.loadCalendar();
        },
      },

      /**
       * Необходимо для реактивного обновления во время записи/отмены
       */
      data: {
        handler(data) {
          if (!data) return;
          this.calendarData = data;
        },
        deep: true,
      },
      meta: {
        handler(meta) {
          if (!meta) return;
          this.pagination = meta;
        },
        deep: true,
      },
      errors: {
        handler(errors) {
          if (!errors?.length) return;
          clearTimeout(this.timerErrors);
          this.timerErrors = setTimeout(() => {
            this.$store.dispatch('calendar/resetErrors');
          }, 9000);
        },
        deep: true,
      },

      breakpoint: {
        handler() {
          this.loadCalendar();
        },
      },
    },

    methods: {
      loadCalendar() {
        const perPage = MEDIA_PER_PAGE[this.breakpoint];
        const lastPerPage = MEDIA_PER_PAGE[mediaQueries.lastBreakpoint];

        /**
         * Необходимо для realtime смены разрешения
         * Запрет загрузки, если требуемое кол-во элементов на страницу не изменилось
         */
        if (perPage === lastPerPage) {
          return;
        }
        const filter = {
          currentPage: 1,
          perPage,
        };
        this.updateCalendar(filter);
      },

      loadCalendarPrevious() {
        if (this.paginationControls.previous.disabled) {
          return;
        }
        const { currentPage } = this.pagination;
        const filter = {
          currentPage: (currentPage === 1) ? -1 : currentPage - 1,
          perPage: MEDIA_PER_PAGE[this.breakpoint],
        };
        this.updateCalendar(filter);
      },

      loadCalendarNext() {
        if (this.paginationControls.next.disabled) {
          return;
        }
        const { currentPage } = this.pagination;
        const filter = {
          currentPage: (currentPage === -1) ? 1 : currentPage + 1,
          perPage: MEDIA_PER_PAGE[this.breakpoint],
        };
        this.updateCalendar(filter);
      },

      async updateCalendar(filter) {
        const { user, userType, appUser } = this;
        if (!user) {
          return;
        }
        this.isLoading = true;
        await this.$store.dispatch('calendar/fetch', {
          userId: user.id || getUserId(),
          appUser,
          userType,
          filter,
        });
        this.calendarData = this.data;
        this.pagination = this.meta;
        // необходимо для отработки css-анимаций
        await asyncDelay(400);
        this.isLoading = false;
        this.isFirstLoad = false;
      },
    },
  };
</script>

<style lang="scss" module>
  .errors {
    margin-top: -20px;
  }

  .content {
    position: relative;
  }

  .loadBox {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba($colorWhite, .75);
    position: absolute;
    @include positionAll(0);
    z-index: 100;
  }

  .controls {
    height: 1px;
    position: relative;
    z-index: 101;
  }

  .buttonPrevious,
  .buttonNext {

    color: $colorGreen;
    width: 60px;
    height: 60px;
    position: absolute;
    top: -85px;
    z-index: 101;
    @include media('xs') {
      top: -16px;
      width: 45px;
      height: 40px;
    }
  }

  .buttonPrevious {
    right: 80px;
    @include media('xs') {
      left: 90px;
    }
  }

  .buttonNext {
    right: 0;
    @include media('xs') {
      right: 90px;
    }
  }

  .buttonIcon {
    font-size: 30px;
  }

  .list {
    display: flex;
    justify-content: space-between;
    @include media('xs') {
margin-top: 40px;
    }
  }

  .item {
    width: 100%;
  }

  .itemOnly {
    max-width: 290px;
    @include marginX(auto);
  }

  .emptyMessage {
    color: $colorGrayDark;
    text-align: center;
  }
</style>
